/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from 'zustand';

interface PageTitleState {
  /**
   * The title the currently-mounted page published for itself, or `null` when
   * no page has published one and the route-pattern fallback should win.
   */
  title: string | null;
  /**
   * Which mount currently owns `title`. A route change mounts the next page
   * before unmounting the previous one, so the outgoing page's cleanup must be
   * able to tell "my title is still up" from "the incoming page has already
   * replaced it".
   *
   * This is a per-mount token, deliberately NOT the title text: two
   * consecutive pages can publish the same string (an admin and a learner view
   * of one course; a group and a course that share a name), and comparing text
   * would let the outgoing page clear the incoming page's title and leave the
   * heading on the generic route label for the rest of its life.
   */
  owner: string | null;
  setTitle: (owner: string, title: string) => void;
  /** Clears only if `owner` still holds the title. */
  clearTitle: (owner: string) => void;
}

export const usePageTitleStore = create<PageTitleState>()((set, get) => ({
  title: null,
  owner: null,
  setTitle: (owner, title) => set({ owner, title }),
  clearTitle: (owner) => {
    if (get().owner === owner) set({ owner: null, title: null });
  },
}));
